import { Badge, Box, Group, ScrollArea, Stack, Text } from '@mantine/core'
import { IconChecks, IconMoodSmile } from '@tabler/icons-react'
import { Link } from '@tanstack/react-router'
import type { WidgetDefinition, WidgetPayload } from '../api/types'
import { EmptyState } from '../components/EmptyState'
import { EMPTY_COPY } from '../components/empty-copy'
import { PRIORITY_COLOURS } from '../components/project/task-format'

type WidgetTask = {
  id: string
  title: string
  project_id: string
  project_name: string
  project_colour: string
  priority: 1 | 2 | 3 | 4
  due_on: string | null
  overdue: boolean
  due_today: boolean
}

/**
 * Renders both the "My tasks" and "Overdue" widgets - they carry the same
 * shape and differ only in what the server put in them.
 *
 * Each row links to its project, because a task you cannot act on from the
 * dashboard is just a reminder that you have work.
 */
export function TaskListWidget({
  definition,
  payload,
}: {
  definition: WidgetDefinition
  payload: WidgetPayload<{ tasks: WidgetTask[] }>
}) {
  const tasks = payload.data.tasks ?? []

  if (tasks.length === 0) {
    const copy = definition.id === 'project.overdue' ? EMPTY_COPY.overdue : EMPTY_COPY.assigned

    return (
      <EmptyState
        compact
        topic={definition.id === 'project.overdue' ? 'deadlines' : 'tasks'}
        icon={definition.id === 'project.overdue' ? <IconMoodSmile /> : <IconChecks />}
        title={copy.title}
        hint={copy.hint}
      />
    )
  }

  return (
    <ScrollArea h="100%" type="auto" offsetScrollbars>
      <Stack gap={2}>
        {tasks.map((task) => (
          <Link
            key={task.id}
            to="/project/$projectId"
            params={{ projectId: task.project_id }}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <Group justify="space-between" wrap="nowrap" gap="sm" py={5}>
              <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                <Box
                  w={7}
                  h={7}
                  bg={`var(--mantine-color-${task.project_colour}-6)`}
                  style={{ borderRadius: 2, flexShrink: 0 }}
                />
                {task.priority < 4 && (
                  <Badge
                    size="xs"
                    variant="outline"
                    color={PRIORITY_COLOURS[task.priority]}
                    style={{ flexShrink: 0 }}
                  >
                    P{task.priority}
                  </Badge>
                )}
                <Text size="sm" truncate="end">
                  {task.title}
                </Text>
              </Group>

              <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                <Text size="xs" c="dimmed" visibleFrom="sm">
                  {task.project_name}
                </Text>
                {task.due_on && (
                  <Badge
                    size="xs"
                    variant={task.overdue ? 'filled' : 'light'}
                    color={task.overdue ? 'red' : task.due_today ? 'orange' : 'gray'}
                  >
                    {formatDue(task)}
                  </Badge>
                )}
              </Group>
            </Group>
          </Link>
        ))}
      </Stack>
    </ScrollArea>
  )
}

function formatDue(task: WidgetTask): string {
  if (task.due_today) return 'Today'
  if (!task.due_on) return ''

  return new Date(`${task.due_on}T00:00:00`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
  })
}
